/**
 * RunCap Types — Runway Capacity Analysis
 *
 * TypeScript interfaces for the three-layer RunCap engine:
 *   Layer 1: IATA ADRM / FAA AC 150 / SAM MTTS
 *   Layer 2: DACAMS trims (congestion, WCF)
 *   Layer 3: Tri-Node Equilibrium
 */

// ==================== Enums ====================

export type CalculationMethod = 'IATA_ADRM' | 'FAA_AC150' | 'SAM_MTTS';
export type WeatherCategory = 'VMC' | 'CAT_I' | 'CAT_II' | 'CAT_IIIA' | 'CAT_IIIB' | 'LVP';
export type SeparationStandard = 'ICAO' | 'RECAT-EU' | 'RECAT-PWS';
export type RunwayMode = 'arrivals_only' | 'departures_only' | 'mixed' | 'segregated';

// ==================== Input Types ====================

export interface AircraftMix {
    cat_a: number;  // Super Heavy %
    cat_b: number;  // Upper Heavy %
    cat_c: number;  // Lower Heavy %
    cat_d: number;  // Upper Medium %
    cat_e: number;  // Lower Medium %
    cat_f: number;  // Light %
}

export interface RunwayConfig {
    runway_id: string;
    length_m: number;
    mode: RunwayMode;
    weather: WeatherCategory;
    separation_std: SeparationStandard;
    aircraft_mix: AircraftMix;
    parallel_runway: boolean;
    parallel_independent: boolean;
    parallel_spacing_m: number;
    rot_arrival_s: number;
    rot_departure_s: number;
    arr_dep_ratio: number;
    // Infrastructure
    n_rets: number;
    n_holding_points: number;
    ils_category: string;
    pct_touch_and_go: number;
    // Method
    method: CalculationMethod;
    // SAM inputs
    sam_mean_speed_kt: number;
    sam_standard_separation_nm: number;
    sam_rsm_nm: number;
    // Kept for backward compat
    rapid_exit_taxiways: number;
    holding_points: number;
}

// ==================== Layer 1 Result Sub-types ====================

export interface FAABaseline {
    mix_index: number;
    c_base: number;
    exit_factor: number;
    touch_factor: number;
    hcr: number;
    pct_arrivals: number;
}

export interface SAMResult {
    total_separation_nm: number;
    mean_speed_kt: number;
    mtts_s: number;
    nldg: number;
    ntko: number;
    trc: number;
}

// ==================== Layer 2: DACAMS Trims ====================

export interface DacamsTrims {
    dacams_am: number;
    congestion_factor: number;
    wcf_score: number;
    beta_w: number;
    capacity_after_congestion: number;
    capacity_after_wcf: number;
}

// ==================== Layer 3: Tri-Node ====================

export interface TriNodeResult {
    controlling_capacity: number;
    bottleneck_node: 'RUNWAY' | 'APRON' | 'TERMINAL';
    mismatch_vector: {
        RRC_waste: number;
        RAC_waste: number;
        PTBC_waste: number;
    };
    balanced_capacity_index: number;
    slot_viability_score: number;
    rgpp: number;
    rgpp_alert: boolean;
    svs_alert: boolean;
    inputs: {
        rrc: number;
        rac_eff: number;
        ptbc: number;
        demand: number;
    };
}

// ==================== Full Result ====================

export interface RunwayCapacityResult {
    runway_id: string;
    method_used: string;
    mode: string;
    weather: string;
    separation_std: string;
    max_arrivals_per_hour: number;
    max_departures_per_hour: number;
    max_mixed_per_hour: number;
    practical_capacity_per_hour: number;
    avg_arrival_separation_s: number;
    avg_departure_separation_s: number;
    min_arr_dep_gap_s: number;
    weather_reduction_factor: number;
    // Method-specific
    faa_baseline: FAABaseline | null;
    sam_result: SAMResult | null;
    // Layer 2
    dacams_trims: DacamsTrims | null;
    dacams_rrc: number | null;
    // Derived
    annual_capacity_estimate: number;
    peak_hour_movements: number;
    sustainable_capacity_per_hour: number;
    notes: string[];
}

export interface RunwayScenarioComparison {
    as_is: RunwayCapacityResult;
    to_be: RunwayCapacityResult;
    delta_arrivals: number;
    delta_departures: number;
    delta_mixed: number;
    delta_practical: number;
    improvement_pct: number;
    notes: string[];
}

// ==================== UI Helpers ====================

export const DEFAULT_AIRCRAFT_MIX: AircraftMix = {
    cat_a: 0, cat_b: 5, cat_c: 25, cat_d: 40, cat_e: 25, cat_f: 5,
};

export const DEFAULT_RUNWAY_CONFIG: RunwayConfig = {
    runway_id: '03R/21L',
    length_m: 3050,
    mode: 'mixed',
    weather: 'VMC',
    separation_std: 'RECAT-EU',
    aircraft_mix: { ...DEFAULT_AIRCRAFT_MIX },
    parallel_runway: false,
    parallel_independent: false,
    parallel_spacing_m: 0,
    rot_arrival_s: 50,
    rot_departure_s: 40,
    arr_dep_ratio: 0.5,
    n_rets: 2,
    n_holding_points: 2,
    ils_category: 'I',
    pct_touch_and_go: 0,
    method: 'IATA_ADRM',
    sam_mean_speed_kt: 130,
    sam_standard_separation_nm: 3.0,
    sam_rsm_nm: 0.5,
    rapid_exit_taxiways: 2,
    holding_points: 2,
};

export const METHOD_LABELS: Record<CalculationMethod, { name: string; desc: string }> = {
    IATA_ADRM: { name: 'IATA ADRM', desc: 'RECAT-EU weighted pair separations' },
    FAA_AC150: { name: 'FAA AC 150/5060-5', desc: 'C_base × E × T with Mix Index' },
    SAM_MTTS: { name: 'SAM / MTTS', desc: 'Mean Weighted Time model' },
};

export const WEATHER_LABELS: Record<WeatherCategory, string> = {
    VMC: 'VMC (Visual)',
    CAT_I: 'CAT I',
    CAT_II: 'CAT II',
    CAT_IIIA: 'CAT IIIA',
    CAT_IIIB: 'CAT IIIB',
    LVP: 'LVP',
};

export const MODE_LABELS: Record<RunwayMode, string> = {
    arrivals_only: 'Arrivals Only',
    departures_only: 'Departures Only',
    mixed: 'Mixed Mode',
    segregated: 'Segregated',
};

export const RECAT_EU_LABELS: Record<string, string> = {
    cat_a: 'A — Super Heavy (A380)',
    cat_b: 'B — Upper Heavy',
    cat_c: 'C — Lower Heavy',
    cat_d: 'D — Upper Medium',
    cat_e: 'E — Lower Medium',
    cat_f: 'F — Light',
};

// DACAMS AM Weight Constants (AP-CAM Block 1, Annex B)
export const DACAMS_AM_WEIGHTS = {
    Light: 0.8,    // RECAT F
    Medium: 1.0,   // RECAT D, E
    Heavy: 1.5,    // RECAT B, C
    Super: 2.5,    // RECAT A
};
